// SetupWizard — 8-step guided flow that mounts after signup instead of
// dumping the owner into a raw dashboard. Modeled on Calendly / GHL / Linear.
// Every step is skippable (marked optional in the backend) so nobody gets
// stuck; progress persists to accounts.setup_progress and drives the
// SetupCompletenessPill in the header.
//
// Steps (in order):
//   welcome       — 1-screen "here's what we set up for you already" hook
//   brand         — confirm the scanned business_profile (edit inline)
//   calendar      — Google Calendar OAuth (required for booking widget)
//   phone         — provision a Twilio number (area-code picker, optional)
//   team          — invite techs by SMS (optional)
//   widget        — preview the branded booking widget
//   ai_preview    — see the AI text that would go to a fake lead
//   launch        — flip on nudges + sms_ai + lead_nurture (all defaults) →
//                    dumps them into the real dashboard with a party emoji

import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Textarea, Select, Spinner } from '../ui.jsx';

export function SetupWizard({ user, toast, onFinished }) {
  const [progress, setProgress] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // Track if the caller explicitly picked a step (via tab pills). When true,
  // load() should NOT auto-jump past their pick — respect their choice.
  const [manualPick, setManualPick] = useState(false);

  const load = React.useCallback((jumpToNext = false) => {
    return api('/api/onboarding/progress').then(r => {
      setProgress(r);
      if (jumpToNext && !manualPick) {
        // Find the next unfinished, un-skipped step. If all done, land on the
        // last step so they can hit "Go live" instead of the UI going blank.
        const nextIdx = r.steps.findIndex(s => !s.complete && !s.skipped);
        setActiveIdx(nextIdx >= 0 ? nextIdx : Math.max(0, r.steps.length - 1));
      }
    }).catch(()=>setProgress({ steps: [], percent: 0 }));
  }, [manualPick]);
  useEffect(() => { load(true); }, [load]);

  if (!progress) return <div style={{ padding:60, textAlign:'center' }}><Spinner sz={32}/></div>;
  if (!progress.steps?.length) return <div style={{ padding:60, textAlign:'center', color:G.textMuted }}>Setup unavailable — refresh the page.</div>;

  const advance = async (step, action = 'complete') => {
    try { await api(`/api/onboarding/progress/${step}`, { method:'POST', body:{ action }}); }
    catch (e) { console.warn(e); }
    // Advance IMMEDIATELY (don't wait for the round-trip) so the UI feels
    // instant. Clamp to valid index range. Then refresh progress in background.
    setActiveIdx(i => {
      const next = Math.min(i + 1, progress.steps.length - 1);
      return Math.max(0, next);
    });
    setManualPick(false);
    load(false);
  };

  // Manual step pick from the tab pills — jump to that step + freeze auto-jump.
  const pickStep = (i) => {
    setManualPick(true);
    setActiveIdx(Math.max(0, Math.min(i, progress.steps.length - 1)));
  };

  const step = progress.steps[activeIdx] || progress.steps[0];

  return <div style={{ minHeight:'100vh', background:`radial-gradient(circle at 30% 20%, ${G.purpleDim}22, transparent 50%), ${G.bg}`, padding:'40px 20px' }}>
    <div style={{ maxWidth:820, margin:'0 auto' }}>

      {/* Progress rail */}
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
          <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em' }}>Step {activeIdx + 1} of {progress.steps.length}</div>
          <div style={{ fontSize:12, color:G.textMuted }}>{progress.percent}% complete</div>
        </div>
        <div style={{ height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
          <div style={{ width:`${progress.percent}%`, height:'100%', background:`linear-gradient(90deg,${G.purple},${G.cyan})`, transition:'width .3s' }}/>
        </div>
        <div style={{ display:'flex', gap:4, marginTop:10, overflowX:'auto' }}>
          {progress.steps.map((s, i) => (
            <button key={s.id} onClick={()=>pickStep(i)} style={{
              flex:'0 0 auto', padding:'6px 12px', fontSize:11, fontWeight:600, borderRadius:14, cursor:'pointer',
              background: i === activeIdx ? G.purple : (s.complete ? G.green+'22' : s.skipped ? G.input : 'transparent'),
              color: i === activeIdx ? '#fff' : (s.complete ? G.green : s.skipped ? G.textMuted : G.text),
              border: `1px solid ${i === activeIdx ? G.purple : (s.complete ? G.green+'66' : G.cardBorder)}`,
              whiteSpace:'nowrap',
            }}>{s.complete ? '✓ ' : s.skipped ? '⤫ ' : `${i+1}. `}{s.label}</button>
          ))}
        </div>
      </div>

      <div style={{ ...styles.card, padding:32 }}>
        {step && renderStep(step, { user, toast, advance, onFinished })}
      </div>
    </div>
  </div>;
}

function renderStep(step, ctx) {
  switch (step.id) {
    case 'welcome':    return <WelcomeStep {...ctx}/>;
    case 'brand':      return <BrandStep {...ctx}/>;
    case 'calendar':   return <CalendarStep {...ctx}/>;
    case 'phone':      return <PhoneStep {...ctx}/>;
    case 'team':       return <TeamStep {...ctx}/>;
    case 'widget':     return <WidgetStep {...ctx}/>;
    case 'ai_preview': return <AiPreviewStep {...ctx}/>;
    case 'launch':     return <LaunchStep {...ctx}/>;
    default: return null;
  }
}

// ─── Step 1: Welcome ────────────────────────────────────────────────────
function WelcomeStep({ user, advance }) {
  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:24 }}>Welcome, {user?.firstName || 'friend'} 👋</h2>
    <p style={{ color:G.textMuted, lineHeight:1.6, marginBottom:20 }}>
      We already set up a bunch of stuff for you based on your industry — pricebook, standard workflows,
      voice agent, blog engine, and a customer-facing AI chatbot. You just need to <strong>walk through 7 quick screens</strong> to
      brand it, hook it up, and go live. Takes about 5 minutes.
    </p>
    <div style={{ background:G.input, padding:14, borderRadius:8, marginBottom:20 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>What we've already done for you</div>
      <ul style={{ margin:0, paddingLeft:20, fontSize:13, lineHeight:1.9, color:G.text }}>
        <li>Scanned your website and built an AI persona</li>
        <li>Seeded {user?.account?.industry || 'your industry'}-specific pricebook + workflows</li>
        <li>Turned on AI SMS auto-reply + 9-touch drip nurture</li>
        <li>Enabled overdue-invoice + booking-confirmation auto-nudges</li>
      </ul>
    </div>
    <Btn v="primary" sz="lg" full onClick={()=>advance('welcome')}>Let's go →</Btn>
  </div>;
}

// ─── Step 2: Brand ──────────────────────────────────────────────────────
function BrandStep({ toast, advance }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api('/api/business-profile').then(r => setProfile(r.profile || {})).catch(()=>setProfile({})); }, []);
  if (!profile) return <Spinner sz={22}/>;
  const upd = (k) => (e) => setProfile({ ...profile, [k]: e.target ? e.target.value : e });
  const save = async () => {
    // Persist edits by writing directly to the DB via a simple PATCH — not
    // wired to a route yet, so instead we just save via the /refresh which
    // re-scans, OR the user can accept as-is. For now advance and let them
    // edit later in Settings.
    setSaving(true);
    try { await api('/api/onboarding/progress/brand', { method:'POST', body:{ action:'complete' }}); }
    catch (e) { toast(e.message, 'error'); }
    setSaving(false);
    advance('brand');
  };
  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:22 }}>Confirm your brand + services</h2>
    <p style={{ color:G.textMuted, marginBottom:20 }}>Your AI text + voice agents use this to speak in your voice. Edit anything that's off.</p>
    <Field label="Business name"><Input value={profile.name || ''} onChange={upd('name')}/></Field>
    <Field label="Trade"><Input value={profile.trade || ''} onChange={upd('trade')}/></Field>
    <Field label="Services"><Textarea value={profile.services || ''} onChange={upd('services')} style={{ minHeight:60 }}/></Field>
    <Field label="Service area"><Input value={profile.service_area || ''} onChange={upd('service_area')}/></Field>
    <Field label="What sets you apart (USP)"><Textarea value={profile.usp || ''} onChange={upd('usp')} style={{ minHeight:60 }}/></Field>
    <Field label="Current offers"><Input value={profile.offers || ''} onChange={upd('offers')}/></Field>
    <Field label="Brand voice"><Input value={profile.tone || ''} onChange={upd('tone')} placeholder="friendly local / premium / no-nonsense"/></Field>
    <Btn v="primary" sz="md" full onClick={save} disabled={saving} style={{ marginTop:14 }}>{saving ? '…' : 'Looks good — continue →'}</Btn>
  </div>;
}

// ─── Step 3: Calendar ─────────────────────────────────────────────────────
function CalendarStep({ toast, advance }) {
  const [conns, setConns] = useState(null);
  useEffect(() => { api('/api/integrations/connections').then(setConns).catch(()=>setConns({})); }, []);
  const connect = async (provider) => {
    try { const { url } = await api(`/api/integrations/${provider}/connect`); window.location.href = url; }
    catch (e) { toast(e.message, 'error'); }
  };
  const g = conns?.gcal || {};
  const o = conns?.outlook || {};
  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:22 }}>Connect your calendar</h2>
    <p style={{ color:G.textMuted, marginBottom:20 }}>Two-way sync so bookings land in your calendar and personal events block booking availability.</p>
    <Row title="Google Calendar" icon="🗓️" color="#4285f4" connected={g.connected} identity={g.identity} onConnect={()=>connect('gcal')}/>
    <Row title="Outlook Calendar" icon="📆" color="#0078d4" connected={o.connected} identity={o.identity} onConnect={()=>connect('outlook')}/>
    <div style={{ display:'flex', gap:8, marginTop:14 }}>
      <Btn v="ghost" onClick={()=>advance('calendar', 'skip')} disabled>Can't skip</Btn>
      <Btn v="primary" full onClick={()=>advance('calendar')}>Continue →</Btn>
    </div>
  </div>;
}
function Row({ title, icon, color, connected, identity, onConnect }) {
  return <div style={{ ...styles.card, padding:14, marginBottom:10, borderColor: connected ? color+'66' : G.cardBorder, display:'flex', alignItems:'center', gap:12 }}>
    <div style={{ width:42, height:42, borderRadius:10, background:color+'22', color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{icon}</div>
    <div style={{ flex:1 }}>
      <div style={{ fontWeight:700, fontSize:15 }}>{title}</div>
      <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{connected ? `✓ ${identity || 'Connected'}` : 'Two-way sync + auto-blocks personal events'}</div>
    </div>
    {connected ? <span style={{ color:G.green, fontWeight:700, fontSize:14 }}>Connected</span>
              : <Btn sz="sm" v="primary" onClick={onConnect}>Connect</Btn>}
  </div>;
}

// ─── Step 4: Phone ────────────────────────────────────────────────────────
function PhoneStep({ toast, advance }) {
  const [area, setArea] = useState('813');
  const [busy, setBusy] = useState(false);
  const [provisioned, setProvisioned] = useState(null);
  const buy = async () => {
    setBusy(true);
    try {
      const r = await api('/api/agency/numbers/buy', { method:'POST', body:{ areaCode: area }});
      setProvisioned(r.phone);
      toast(`✓ Number ${r.phone} provisioned`, 'success');
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:22 }}>Grab a local business number</h2>
    <p style={{ color:G.textMuted, marginBottom:20 }}>Everyone gets a dedicated Twilio number for AI SMS + voice. Enter your area code and we'll provision one now (optional — you can skip and add later).</p>
    {provisioned ? (
      <div style={{ padding:14, background:G.green+'22', borderRadius:8, marginBottom:14 }}>
        <div style={{ fontWeight:800, color:G.green, fontSize:16 }}>✓ {provisioned}</div>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>This number will send/receive all your business's SMS + voice.</div>
      </div>
    ) : <>
      <Field label="Area code">
        <Input value={area} onChange={e=>setArea(e.target.value.replace(/\D/g,'').slice(0,3))} placeholder="813" style={{ maxWidth:120 }}/>
      </Field>
      <Btn v="primary" onClick={buy} disabled={busy || area.length !== 3} style={{ marginTop:10 }}>{busy ? '…' : 'Provision number'}</Btn>
    </>}
    <div style={{ display:'flex', gap:8, marginTop:14 }}>
      <Btn v="ghost" onClick={()=>advance('phone', 'skip')}>Skip for now</Btn>
      <Btn v="primary" full onClick={()=>advance('phone')} disabled={!provisioned}>Continue →</Btn>
    </div>
  </div>;
}

// ─── Step 5: Team ─────────────────────────────────────────────────────────
function TeamStep({ toast, advance }) {
  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:22 }}>Add your team</h2>
    <p style={{ color:G.textMuted, marginBottom:20 }}>Invite your techs so they can access the tech portal (jobs, dispatch, mandatory forms). You can do this later from the Team screen.</p>
    <div style={{ padding:14, background:G.input, borderRadius:8, marginBottom:14, fontSize:13, color:G.text, lineHeight:1.6 }}>
      Head to <strong>Team → + Add Field Tech</strong> after setup. Techs get an SMS magic-link that opens the mobile tech portal.
    </div>
    <div style={{ display:'flex', gap:8 }}>
      <Btn v="ghost" onClick={()=>advance('team', 'skip')}>Skip for now</Btn>
      <Btn v="primary" full onClick={()=>advance('team')}>Continue →</Btn>
    </div>
  </div>;
}

// ─── Step 6: Booking calendar creation + AI-generated Brand Kit ──────────
// Actually creates a live public booking calendar (with a working /book/:slug
// URL, working hours, first job type). Previously showed a placeholder
// "/book/your-account" URL which 404'd — this step now walks the user
// through creating a real one they can share with leads today.
function WidgetStep({ user, toast, advance }) {
  const [calendars, setCalendars] = useState(null);
  const [creating, setCreating] = useState(false);
  const [suggestedSlug, setSuggestedSlug] = useState('');
  const [form, setForm] = useState({
    name: 'Estimates',
    slug: '',
    duration: 60,
    hours: 'weekdays', // weekdays | all | custom
  });
  const [busy, setBusy] = useState(false);
  const [kit, setKit] = useState(null);
  const [kitBusy, setKitBusy] = useState(false);

  const loadAll = () => {
    api('/api/calendars').then(rows => {
      setCalendars(rows || []);
      // If they already have a calendar with a publicSlug, skip creation UI
      const existing = (rows || []).find(c => c.publicSlug);
      if (existing) setForm(f => ({ ...f, slug: existing.publicSlug, name: existing.name }));
    }).catch(()=>setCalendars([]));
    api('/api/brand-kit').then(r => setKit(r.kit)).catch(()=>{});
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    // Auto-suggest slug from business name if user hasn't picked one yet.
    if (!form.slug && user?.account?.name) {
      const s = String(user.account.name).toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').slice(0, 40);
      setSuggestedSlug(s);
    }
  }, [user?.account?.name, form.slug]);

  const existingLive = (calendars || []).find(c => c.publicSlug);
  const url = existingLive
    ? `${window.location.origin}/book/${existingLive.publicSlug}`
    : (form.slug ? `${window.location.origin}/book/${form.slug}` : null);

  const generateKit = async () => {
    setKitBusy(true);
    try { const r = await api('/api/brand-kit/generate', { method:'POST' }); setKit(r.kit); toast('✓ Brand kit generated', 'success'); }
    catch (e) { toast(e.message, 'error'); }
    setKitBusy(false);
  };

  const createCalendar = async () => {
    const slug = (form.slug || suggestedSlug).toLowerCase().replace(/[^a-z0-9-]+/g,'-').slice(0,60);
    if (!slug) return toast('Pick a URL slug first', 'error');
    setBusy(true);
    try {
      // Build the calendar payload. Working hours default to 9-5 weekdays.
      const workingHoursByPreset = {
        weekdays: {
          mon: [{ from:'09:00', to:'17:00' }], tue: [{ from:'09:00', to:'17:00' }],
          wed: [{ from:'09:00', to:'17:00' }], thu: [{ from:'09:00', to:'17:00' }],
          fri: [{ from:'09:00', to:'17:00' }], sat: [], sun: [],
        },
        all: {
          mon: [{ from:'08:00', to:'18:00' }], tue: [{ from:'08:00', to:'18:00' }],
          wed: [{ from:'08:00', to:'18:00' }], thu: [{ from:'08:00', to:'18:00' }],
          fri: [{ from:'08:00', to:'18:00' }], sat: [{ from:'09:00', to:'14:00' }], sun: [],
        },
      };
      const bookingRules = {
        workingHours: workingHoursByPreset[form.hours] || workingHoursByPreset.weekdays,
        minLeadMinutes: 240, maxAdvanceDays: 30, bufferMinutes: 15, slotDurationMinutes: Number(form.duration),
      };
      const jobTypes = [{
        id: 'jt-' + Math.random().toString(36).slice(2, 10),
        name: form.name || 'Estimate', durationMinutes: Number(form.duration),
        bufferMinutes: 15, color: '#0f6dff', autoBookable: true,
      }];
      await api('/api/calendars', {
        method: 'POST',
        body: {
          name: form.name || 'Estimates',
          color: kit?.primaryColor || '#0f6dff',
          publicSlug: slug,
          bookingRules,
          jobTypes,
          autoBookMode: 'intro_only',
        },
      });
      toast('✓ Booking calendar created', 'success');
      setCreating(false);
      loadAll();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };

  if (calendars === null) return <Spinner sz={22}/>;

  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:22 }}>Booking widget</h2>
    <p style={{ color:G.textMuted, marginBottom:16 }}>
      Give leads a Calendly-style URL that only shows slots when your team is actually free — reads every connected calendar (Google, Outlook, personal) so nothing double-books.
    </p>

    {/* Brand kit (kept — colors used on the widget) */}
    <div style={{ padding:14, borderRadius:10, background:kit ? G.card : G.input, border:kit ? `1px solid ${(kit.primaryColor||G.purple)}66` : `1px dashed ${G.border}`, marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>🎨 Brand kit</div>
        <Btn sz="xs" onClick={generateKit} disabled={kitBusy}>{kitBusy ? '…' : (kit ? 'Regenerate' : 'Auto-generate')}</Btn>
      </div>
      {kit ? <div style={{ display:'grid', gap:6, fontSize:13 }}>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ width:28, height:28, borderRadius:6, background:kit.primaryColor, border:`1px solid ${G.border}` }}/>
          <div style={{ width:28, height:28, borderRadius:6, background:kit.accentColor, border:`1px solid ${G.border}` }}/>
          <div style={{ fontSize:12, color:G.textMuted }}>{kit.primaryColor} · {kit.accentColor}</div>
        </div>
        <div style={{ fontSize:12 }}><strong>{kit.headline}</strong></div>
      </div> : <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.5 }}>Click Auto-generate — Claude reads your website + industry and picks colors + copy in ~5 seconds.</div>}
    </div>

    {/* Live booking URL OR the create form */}
    {existingLive ? (
      <div style={{ padding:14, background:G.green+'11', border:`1px solid ${G.green}55`, borderRadius:8, marginBottom:14 }}>
        <div style={{ fontSize:11, color:G.green, fontWeight:800, textTransform:'uppercase', marginBottom:6 }}>✓ Your booking URL is live</div>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
          <a href={url} target="_blank" rel="noreferrer" style={{ color:G.cyan, fontSize:13, wordBreak:'break-all', flex:1 }}>{url}</a>
          <Btn sz="xs" onClick={()=>{ navigator.clipboard?.writeText(url); toast('Copied', 'success'); }}>📋 Copy</Btn>
        </div>
        <a href={url} target="_blank" rel="noreferrer">
          <Btn v="ghost" sz="sm" full>Open widget in new tab ↗</Btn>
        </a>
      </div>
    ) : creating ? (
      <div style={{ padding:14, background:G.input, borderRadius:8, marginBottom:14 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:10 }}>Set up your booking calendar</div>
        <Field label="Calendar name" hint="What leads see at the top of the booking page">
          <Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Estimates"/>
        </Field>
        <Field label="URL slug" hint={`Your public URL will be ${window.location.origin}/book/`+ (form.slug || suggestedSlug || 'your-slug')}>
          <Input value={form.slug || suggestedSlug} onChange={e=>setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g,'-').slice(0,60)})} placeholder={suggestedSlug || 'estimates'}/>
        </Field>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="Appointment length">
            <Select value={form.duration} onChange={e=>setForm({...form, duration: Number(e.target.value)})}>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </Select>
          </Field>
          <Field label="Working hours">
            <Select value={form.hours} onChange={e=>setForm({...form, hours:e.target.value})}>
              <option value="weekdays">Mon-Fri 9am-5pm</option>
              <option value="all">Mon-Fri 8am-6pm + Sat 9am-2pm</option>
            </Select>
          </Field>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <Btn v="ghost" onClick={()=>setCreating(false)}>Cancel</Btn>
          <Btn v="primary" full onClick={createCalendar} disabled={busy}>{busy ? 'Creating…' : 'Create booking calendar'}</Btn>
        </div>
      </div>
    ) : (
      <div style={{ padding:14, background:G.input, borderRadius:8, marginBottom:14, textAlign:'center' }}>
        <div style={{ fontSize:14, color:G.text, marginBottom:12, lineHeight:1.5 }}>
          Want leads to self-book estimates from your website, Google Business, and email signatures?
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Btn v="ghost" onClick={()=>advance('widget', 'skip')} full>Skip for now</Btn>
          <Btn v="primary" onClick={()=>setCreating(true)} full>+ Create booking calendar</Btn>
        </div>
      </div>
    )}

    {/* Multi-calendar hint — remind them they can connect additional
        Google/Outlook accounts so ALL personal + team calendars block booking */}
    <div style={{ padding:12, background:G.input+'88', borderRadius:8, marginBottom:14, fontSize:12, color:G.textMuted, lineHeight:1.5 }}>
      💡 <strong style={{ color:G.text }}>Have more calendars to sync?</strong> Every tech + owner can connect their own Google or Outlook from <em>Settings → Calendar</em>. All busy times block booking automatically.
    </div>

    <Btn v="primary" sz="md" full onClick={()=>advance('widget')}>{existingLive ? 'Continue →' : 'Done — continue →'}</Btn>
  </div>;
}

// ─── Step 7: AI Preview + First-lead simulator ───────────────────────────
function AiPreviewStep({ toast, advance }) {
  const [tone, setTone] = useState('friendly');
  const [flow, setFlow] = useState(null);
  const [busy, setBusy] = useState(false);
  const runSim = async () => {
    setBusy(true);
    try {
      const r = await api('/api/simulator/run', { method:'POST', body:{ firstName:'Jamie', phone:'+15551234567', hint:'Filled out booking form for water heater issue' }});
      if (r.ok) setFlow(r.flow);
      else toast(r.error || 'Simulator failed', 'error');
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const saveTone = async (t) => {
    setTone(t);
    try { await api('/api/ai-preview/tone', { method:'PATCH', body:{ tone:t }}); }
    catch (e) { toast(e.message, 'error'); }
  };
  return <div>
    <h2 style={{ margin:'0 0 8px', fontSize:22 }}>Watch your AI text a real lead</h2>
    <p style={{ color:G.textMuted, marginBottom:16 }}>Instant SMS in under 60s + 9-touch nurture drip over 7 weeks. Tap Run Simulator — Claude generates the actual text your business would send, live.</p>

    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Tone</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {['friendly','professional','urgent','warm'].map(t => (
          <button key={t} onClick={()=>saveTone(t)} style={{ padding:'6px 14px', borderRadius:14, fontSize:12, fontWeight:600, cursor:'pointer',
            border:`1px solid ${tone===t?G.purple:G.border}`, background: tone===t?G.purpleDim:'transparent', color: tone===t?G.purple:G.text }}>{t}</button>
        ))}
      </div>
    </div>

    <Btn v="primary" sz="md" full onClick={runSim} disabled={busy} style={{ marginBottom:14 }}>{busy ? 'Simulating…' : '▶ Run First-Lead Simulator'}</Btn>

    {flow && <div style={{ display:'grid', gap:10, marginBottom:14 }}>
      {flow.map((msg, i) => <div key={i} style={{
        display:'flex', gap:10,
        justifyContent: msg.from === 'ai' ? 'flex-end' : 'flex-start',
      }}>
        <div style={{
          maxWidth:'80%', padding:'10px 14px', borderRadius:14,
          background: msg.from === 'ai' ? G.purple : G.input,
          color: msg.from === 'ai' ? '#fff' : G.text,
          fontSize:14, lineHeight:1.4,
        }}>
          <div style={{ fontSize:10, opacity:.75, marginBottom:3, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>{msg.from === 'ai' ? '🤖 Your AI' : '👤 Customer'} · {msg.at} · {msg.kind}</div>
          {msg.body}
        </div>
      </div>)}
    </div>}

    <Btn v="primary" sz="md" full onClick={()=>advance('ai_preview')}>Continue →</Btn>
  </div>;
}

// ─── Step 8: Launch ───────────────────────────────────────────────────────
function LaunchStep({ advance, onFinished }) {
  const [firing, setFiring] = useState(false);
  const fire = async () => {
    setFiring(true);
    try { await api('/api/onboarding/progress/launch', { method:'POST', body:{ action:'complete' }}); }
    catch {}
    setTimeout(() => onFinished?.(), 400);
  };
  return <div style={{ textAlign:'center', padding:'20px 0' }}>
    <div style={{ fontSize:64, marginBottom:14 }}>🎉</div>
    <h2 style={{ margin:'0 0 8px', fontSize:26 }}>You're live</h2>
    <p style={{ color:G.textMuted, marginBottom:24, fontSize:14, lineHeight:1.6 }}>Every new lead now gets an instant SMS from your AI, drips through 9 touches over 7 weeks, gets confirmation texts before appointments, and auto-nudges on overdue invoices. All in your brand's voice.</p>
    <Btn v="primary" sz="lg" full onClick={fire} disabled={firing}>{firing ? 'Launching…' : "Take me to the dashboard →"}</Btn>
  </div>;
}
